import { useState, type ImgHTMLAttributes, type ReactNode } from 'react';
import { getOptimizedMspLogoUrl, MSP_LOGO_PRESETS } from '@/lib/mspLogo';

interface MspLogoImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Originele logo_url uit de database. */
  logoUrl: string | null | undefined;
  /** Weergavecontext: bepaalt de gevraagde intrinsieke grootte. */
  variant?: keyof typeof MSP_LOGO_PRESETS;
  /** Wordt gerenderd als er geen logo is of als ook het origineel faalt. */
  fallback: ReactNode;
}

/**
 * MSP-logo met geoptimaliseerde transform-URL en eenmalige onError-fallback
 * naar de originele logo_url. Faalt ook het origineel, dan volgt de placeholder.
 * Er wordt nooit opnieuw geprobeerd (geen retry-loop).
 */
export function MspLogoImage({ logoUrl, variant = 'card', fallback, ...imgProps }: MspLogoImageProps) {
  const [stage, setStage] = useState<'optimized' | 'original' | 'failed'>('optimized');

  if (!logoUrl || stage === 'failed') return <>{fallback}</>;

  const optimized = getOptimizedMspLogoUrl(logoUrl, MSP_LOGO_PRESETS[variant]) ?? logoUrl;
  const src = stage === 'optimized' ? optimized : logoUrl;

  return (
    <img
      {...imgProps}
      src={src}
      onError={() => {
        setStage((prev) => {
          if (prev === 'optimized' && optimized !== logoUrl) return 'original';
          return 'failed';
        });
      }}
    />
  );
}
